/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-09-11
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
import React from "react";
import { ExternalLink } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import {
  FADEL_BANNER,
  FADEL_COPY,
  FADEL_LOGO,
  FADEL_SCREENS,
  FADEL_URL,
  FADEL_VERTICALS,
  fadelText,
} from "../lib/fadel";

/**
 * Annonce du lancement de FaDel Delivery, version site UK.
 *
 * Le pendant Tchad vit dans `tchad/components/FadelLaunch.js` : meme contenu,
 * mais la palette du Tchad est or sur bleu nuit quand celle du UK est cyan sur
 * ardoise. Un composant unique pilote par une prop de theme melangeait les deux
 * chartes dans un fichier et rendait chaque retouche risquee des deux cotes ;
 * les visuels et les textes, eux, restent partages via `lib/fadel.js`.
 *
 * Le bandeau est place sur fond sombre : les captures sont detourees, et leur
 * ombre portee ne tiendrait pas sur le blanc des sections voisines.
 */
const FadelLaunch = () => {
  const { lang } = useLang();
  const text = (entry) => fadelText(entry, lang);

  return (
    <section
      data-testid="home-fadel-launch"
      className="relative overflow-hidden bg-slate-950 text-white"
    >
      <div className="absolute inset-0 bg-grid-slate opacity-30" aria-hidden="true" />
      <div
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent"
        aria-hidden="true"
      />

      <div className="relative max-w-site mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-medium text-cyan-300 backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse-ring" />
              {text(FADEL_COPY.badge)}
            </span>

            <img
              src={FADEL_LOGO}
              alt="FaDel Delivery"
              width="760"
              height="258"
              loading="lazy"
              className="mt-6 h-16 w-auto rounded-md sm:h-20"
            />

            <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]">
              {text(FADEL_COPY.title)}
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              {text(FADEL_COPY.lead)}
            </p>

            <a
              href={FADEL_URL}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="fadel-launch-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold px-6 py-3 transition-colors"
            >
              {text(FADEL_COPY.cta)}
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>

          <div className="lg:col-span-7">
            <img
              src={FADEL_BANNER}
              alt=""
              aria-hidden="true"
              width="1280"
              height="619"
              loading="lazy"
              className="w-full rounded-xl border border-white/10 shadow-2xl"
            />
          </div>
        </div>

        {/* Verticales : les pictogrammes de la fiche applicative disent en un
            coup d'oeil l'etendue de la plateforme, ce qu'un paragraphe met
            trois lignes a faire. */}
        <h3 className="mt-20 text-xs font-semibold uppercase tracking-widest text-cyan-300">
          {text(FADEL_COPY.verticalsTitle)}
        </h3>
        <ul className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-8">
          {FADEL_VERTICALS.map(({ src, label }) => (
            <li
              key={src}
              data-testid="fadel-vertical"
              className="flex flex-col items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-4 text-center transition-colors hover:border-cyan-400/40 hover:bg-white/[0.06]"
            >
              <img
                src={src}
                alt=""
                aria-hidden="true"
                width="320"
                height="320"
                loading="lazy"
                className="h-14 w-auto"
              />
              <span className="text-xs font-medium text-slate-300">{text(label)}</span>
            </li>
          ))}
        </ul>

        {/* Captures : defilement horizontal plutot qu'une grille. Neuf ecrans
            en grille prendraient toute la hauteur d'une page d'accueil, alors
            qu'ils ne sont qu'un apercu. */}
        <h3 className="mt-16 text-xs font-semibold uppercase tracking-widest text-cyan-300">
          {text(FADEL_COPY.screensTitle)}
        </h3>
        <div className="mt-6 -mx-4 overflow-x-auto px-4 pb-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <ul className="flex w-max gap-6">
            {FADEL_SCREENS.map(({ src, caption }) => (
              <li key={src} data-testid="fadel-screen" className="w-44 shrink-0 sm:w-52">
                {/* La legende est juste en dessous, en texte visible :
                    laisser le meme libelle en `alt` la ferait annoncer deux
                    fois de suite par un lecteur d'ecran. */}
                <img
                  src={src}
                  alt=""
                  aria-hidden="true"
                  width="550"
                  height="1100"
                  loading="lazy"
                  className="w-full drop-shadow-[0_16px_28px_rgba(0,0,0,0.55)]"
                />
                <p className="mt-4 text-start text-xs leading-relaxed text-slate-400">{text(caption)}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default FadelLaunch;
